import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtMoney } from "@/lib/utils";
import { Field } from "@/components/Field";
import { plainError } from "@/lib/plain-error";

interface OrderCategory { id: string; name: string; }
interface Vendor { id: string; name: string; }
interface OrderRequest {
  id: string; request_id: string; requester_id: string;
  category_id: string | null; item_name: string; quantity: number | null;
  unit_of_measurement: string | null; estimated_cost: number | null;
  vendor_id: string | null; reason_for_request: string | null;
  urgency: "standard" | "emergency";
  approval_status: "pending" | "approved" | "rejected" | "ordered" | "received" | "invoiced" | "cancelled";
  rejection_reason: string | null;
  order_date: string | null; actual_cost: number | null;
  received_date: string | null; invoice_number: string | null;
  notes: string | null; created_at: string;
}

export function Orders() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<OrderRequest[]>([]);
  const [cats, setCats] = useState<OrderCategory[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    category_id: "", item_name: "", quantity: "", unit_of_measurement: "",
    estimated_cost: "", vendor_id: "", reason_for_request: "", urgency: "standard",
  });
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ quantity?: string; estimated_cost?: string }>({});

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const [{ data: r }, { data: c }, { data: v }] = await Promise.all([
      supabase.from("hq_order_requests").select("*").eq("requester_id", employee.id).order("created_at", { ascending: false }),
      supabase.from("hq_order_categories").select("id,name").order("name"),
      supabase.from("vendors_visible").select("id,name").eq("status", "active").order("name"),
    ]);
    setRows((r as OrderRequest[]) ?? []);
    setCats((c as OrderCategory[]) ?? []);
    setVendors((v as Vendor[]) ?? []);
  }

  /**
   * HQ-ORDER-001. The form took whatever was typed: quantity -2 at a cost of
   * -5 persisted, displayed as -2 and -5 in both views, and was approved. The
   * same rule the database now enforces
   * (sql/2027-01-05_order_request_amounts.sql) is checked here so the person
   * gets a field error instead of a constraint message, and keeps what they
   * typed.
   */
  function amountErrors(f: typeof form): { quantity?: string; estimated_cost?: string } {
    const out: { quantity?: string; estimated_cost?: string } = {};
    if (f.quantity.trim()) {
      const q = Number(f.quantity);
      if (!Number.isFinite(q)) out.quantity = "That is not a number.";
      else if (q <= 0) out.quantity = "Quantity has to be more than zero.";
    }
    if (f.estimated_cost.trim()) {
      const c = Number(f.estimated_cost);
      if (!Number.isFinite(c)) out.estimated_cost = "That is not a number.";
      else if (c < 0) out.estimated_cost = "A cost cannot be negative.";
    }
    return out;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    const bad = amountErrors(form);
    setFieldErrors(bad);
    // Nothing is sent and nothing is cleared — the form keeps its contents so
    // the number can be corrected rather than retyped.
    if (Object.keys(bad).length > 0) return;
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_order_requests").insert({
      requester_id: employee.id,
      category_id: form.category_id || null,
      item_name: form.item_name,
      quantity: form.quantity ? Number(form.quantity) : null,
      unit_of_measurement: form.unit_of_measurement || null,
      estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : null,
      vendor_id: form.vendor_id || null,
      reason_for_request: form.reason_for_request || null,
      urgency: form.urgency,
    });
    setBusy(false);
    if (error) setError(plainError(error, "submit that request"));
    else {
      setShowNew(false); setFieldErrors({});
      setForm({ category_id: "", item_name: "", quantity: "", unit_of_measurement: "", estimated_cost: "", vendor_id: "", reason_for_request: "", urgency: "standard" });
      await load();
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Order Requests</h1>
        <button onClick={() => setShowNew(s => !s)} className="btn">+ Request</button>
      </div>

      {showNew && (
        <div className="card">
          {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="label">Item *</label><input required className="input" value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} placeholder="What do you need?" autoFocus /></div>
            <div><label className="label">Category</label><select className="input" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}><option value="">—</option>{cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div><label className="label">Vendor</label><select className="input" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })}><option value="">—</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></div>
            <Field label="Quantity" type="number" step="0.01" min="0" value={form.quantity}
              error={fieldErrors.quantity}
              onChange={e => { setForm({ ...form, quantity: e.target.value }); setFieldErrors(x => ({ ...x, quantity: undefined })); }} />
            <div><label className="label">Unit</label><input className="input" value={form.unit_of_measurement} onChange={e => setForm({ ...form, unit_of_measurement: e.target.value })} placeholder="lbs, units, boxes…" /></div>
            <Field label="Estimated cost ($)" type="number" step="0.01" min="0" value={form.estimated_cost}
              error={fieldErrors.estimated_cost}
              onChange={e => { setForm({ ...form, estimated_cost: e.target.value }); setFieldErrors(x => ({ ...x, estimated_cost: undefined })); }} />
            <div><label className="label">Urgency</label><select className="input" value={form.urgency} onChange={e => setForm({ ...form, urgency: e.target.value })}><option value="standard">Standard</option><option value="emergency">🚨 Emergency</option></select></div>
            <div className="sm:col-span-2"><label className="label">Reason</label><textarea className="input" rows={2} value={form.reason_for_request} onChange={e => setForm({ ...form, reason_for_request: e.target.value })} /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="btn" disabled={busy}>{busy ? "Submitting…" : "Submit"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card overflow-x-auto">
        {rows.length === 0 ? <p className="py-8 text-center text-sm text-gray2">No requests yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2"><th className="py-2">ID</th><th>Item</th><th>Qty</th><th className="text-right">Est. cost</th><th>Urgency</th><th>Status</th><th>When</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-line/70">
                  <td className="py-2 font-mono text-xs">{r.request_id}</td>
                  <td className="py-2">{r.item_name}</td>
                  <td className="py-2 text-gray2">{r.quantity ?? "—"} {r.unit_of_measurement ?? ""}</td>
                  <td className="py-2 text-right">{fmtMoney(r.estimated_cost)}</td>
                  <td className="py-2">{r.urgency === "emergency" ? <span className="pill pill-bad">🚨</span> : <span className="text-gray2 text-xs">std</span>}</td>
                  <td className="py-2"><span className={`pill ${statusPill(r.approval_status)}`}>{r.approval_status.replace(/_/g, " ")}</span></td>
                  <td className="py-2 text-gray2 text-xs">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function OrdersAdmin() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<OrderRequest[]>([]);
  const [cats, setCats] = useState<OrderCategory[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [emps, setEmps] = useState<{ id: string; first_name: string | null; last_name: string | null; email: string }[]>([]);
  const [filter, setFilter] = useState<"all" | OrderRequest["approval_status"]>("pending");

  useEffect(() => { void load(); }, []);
  async function load() {
    const [{ data: r }, { data: c }, { data: v }, { data: e }] = await Promise.all([
      supabase.from("hq_order_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("hq_order_categories").select("id,name"),
      supabase.from("vendors_visible").select("id,name"),
      supabase.from("hq_employees").select("id,first_name,last_name,email"),
    ]);
    setRows((r as OrderRequest[]) ?? []);
    setCats((c as OrderCategory[]) ?? []);
    setVendors((v as Vendor[]) ?? []);
    setEmps((e as { id: string; first_name: string | null; last_name: string | null; email: string }[]) ?? []);
  }
  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const catById = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.name])), [cats]);
  const vendById = useMemo(() => Object.fromEntries(vendors.map(v => [v.id, v.name])), [vendors]);

  const filtered = filter === "all" ? rows : rows.filter(r => r.approval_status === filter);

  async function approve(r: OrderRequest) {
    if (!employee) return;
    await supabase.from("hq_order_requests").update({ approval_status: "approved", approved_by: employee.id, approved_at: new Date().toISOString() }).eq("id", r.id);
    await load();
  }
  async function reject(r: OrderRequest) {
    const reason = prompt("Why are you rejecting this order?"); if (reason === null) return;
    if (!employee) return;
    await supabase.from("hq_order_requests").update({ approval_status: "rejected", rejection_reason: reason || null, approved_by: employee.id, approved_at: new Date().toISOString() }).eq("id", r.id);
    await load();
  }
  async function markOrdered(r: OrderRequest) {
    const cost = prompt("Actual cost (optional):", r.estimated_cost?.toString() ?? "");
    const inv = prompt("Invoice / PO number (optional):");
    await supabase.from("hq_order_requests").update({
      approval_status: "ordered", order_date: new Date().toISOString().slice(0, 10),
      actual_cost: cost ? Number(cost) : null, invoice_number: inv || null,
    }).eq("id", r.id); await load();
  }
  async function markReceived(r: OrderRequest) {
    await supabase.from("hq_order_requests").update({ approval_status: "received", received_date: new Date().toISOString().slice(0, 10) }).eq("id", r.id); await load();
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Order Requests</h1>
      <div className="flex flex-wrap gap-2">
        {(["pending","approved","ordered","received","rejected","all"] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn btn-sm ${filter === s ? "" : "btn-ghost"}`}>
            {s} {s !== "all" && `(${rows.filter(r => r.approval_status === s).length})`}
          </button>
        ))}
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2"><th className="py-2">ID</th><th>Requester</th><th>Item</th><th>Category</th><th>Vendor</th><th className="text-right">Cost</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map(r => {
              const e = empById[r.requester_id];
              return (
                <tr key={r.id} className="border-b border-line/70">
                  <td className="py-2 font-mono text-xs">{r.request_id}{r.urgency === "emergency" && <span className="ml-1 pill pill-bad">🚨</span>}</td>
                  <td className="py-2">{e ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim() || e.email : "—"}</td>
                  <td className="py-2">{r.item_name}<div className="text-xs text-gray2">{r.quantity ?? ""} {r.unit_of_measurement ?? ""}</div></td>
                  <td className="py-2 text-gray2">{r.category_id ? catById[r.category_id] : "—"}</td>
                  <td className="py-2 text-gray2">{r.vendor_id ? vendById[r.vendor_id] : "—"}</td>
                  <td className="py-2 text-right">{fmtMoney(r.actual_cost ?? r.estimated_cost)}</td>
                  <td className="py-2"><span className={`pill ${statusPill(r.approval_status)}`}>{r.approval_status.replace(/_/g, " ")}</span></td>
                  <td className="py-2 text-right space-x-1">
                    {r.approval_status === "pending" && (
                      <>
                        <button onClick={() => approve(r)} className="btn btn-sm">Approve</button>
                        <button onClick={() => reject(r)} className="btn btn-sm btn-ghost text-bad ring-bad">Reject</button>
                      </>
                    )}
                    {r.approval_status === "approved" && <button onClick={() => markOrdered(r)} className="btn btn-sm">Mark ordered</button>}
                    {r.approval_status === "ordered" && <button onClick={() => markReceived(r)} className="btn btn-sm">Received</button>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-sm text-gray2">No matching orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function statusPill(s: OrderRequest["approval_status"]): string {
  if (s === "approved" || s === "received") return "pill-ok";
  if (s === "rejected" || s === "cancelled") return "pill-bad";
  if (s === "ordered" || s === "invoiced") return "pill-mint";
  return "pill-warn";
}
